import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useRef, useId } from "react";

/**
 * Modal sidebar / drawer (right side).
 *
 * Props:
 * - open: boolean (required)
 * - onClose: () => void (required)
 * - title: string (optional)
 * - widthClass: Tailwind width class for the panel (default "w-96")
 * - children: content inside the drawer
 * - closeOnOverlayClick: boolean (default true)
 */

interface ModalDrawerProps {
  open: boolean;
  onClose: () => void;
  iconTitle?: IconProp;
  title?: string;
  widthClass?: string; // Tailwind width class, e.g. "w-96"
  children?: React.ReactNode;
  closeOnOverlayClick?: boolean;
}

export default function ModalDrawer({
  open,
  onClose,
  iconTitle,
  title,
  widthClass = "w-96",
  children,
  closeOnOverlayClick = true,
}: ModalDrawerProps) {
  const panelRef = useRef(null);
  const previouslyFocusedRef = useRef(null);
  const titleId = useId();
  const descId = useId();

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose?.();
      }
      // Simple focus trap
      if (e.key === "Tab" && panelRef.current) {
        const focusables = getFocusable(panelRef.current);
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];

        if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        } else if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      }
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [open, onClose]);

  // Manage focus in/out + restore on close
  useEffect(() => {
    if (open) {
      previouslyFocusedRef.current = document.activeElement;
      // delay to after mount so the panel exists
      const t = setTimeout(() => {
        const focusables = getFocusable(panelRef.current);
        (focusables[0] || panelRef.current)?.focus();
      }, 0);
      return () => clearTimeout(t);
    } else if (previouslyFocusedRef.current instanceof HTMLElement) {
      previouslyFocusedRef.current.focus({ preventScroll: true });
    }
  }, [open]);

  // Prevent background scroll
  useEffect(() => {
    if (!open) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = original; };
  }, [open]);

  // Helper
  function getFocusable(root) {
    if (!root) return [];
    const selectors = [
      "a[href]",
      "button:not([disabled])",
      "textarea:not([disabled])",
      "input:not([disabled])",
      "select:not([disabled])",
      "[tabindex]:not([tabindex='-1'])",
    ].join(",");
    return Array.from(root.querySelectorAll(selectors))
      .filter((el) => !el.hasAttribute("disabled") && !el.getAttribute("aria-hidden"));
  }

  // Click outside
  const handleOverlayClick = (e) => {
    if (!closeOnOverlayClick) return;
    // Ensure clicks inside the panel don't close
    if (panelRef.current && panelRef.current.contains(e.target)) return;
    onClose?.();
  };

  return (
    <div
      aria-hidden={!open}
      className={`fixed inset-0 z-50 ${open ? "" : "pointer-events-none"}`}
    >
      {/* Overlay */}
      <div
        onMouseDown={handleOverlayClick}
        className={`absolute inset-0 bg-black/50 transition-opacity duration-300 ease-out
        ${open ? "opacity-100" : "opacity-0"}`}
      />

      {/* Drawer panel */}
      <aside
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-describedby={descId}
        tabIndex={-1}
        className={`absolute right-0 top-0 h-full bg-white shadow-xl outline-none
        transition-transform duration-300 ease-out overflow-y-auto ${widthClass}
        ${open ? "translate-x-0" : "translate-x-full"}
        `}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between border-b px-4 py-3 bg-white/95 backdrop-blur">
          <h2 id={titleId} className="text-lg font-semibold">
            {iconTitle && <FontAwesomeIcon icon={iconTitle} className="pr-2" />}
            {title || "Panel"}
          </h2>
          <button
            onClick={onClose}
            className="inline-flex items-center rounded-md px-2 py-1 text-sm font-medium
                       hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-black/30"
            aria-label="Close panel"
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div id={descId} className="p-4">
          {children}
        </div>
      </aside>
    </div>
  );
}
